import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NavShell } from "@/components/NavShell";
import { Check, Zap, ArrowRight, Loader2 } from "lucide-react";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "$9",
    period: "/month",
    credits: "100 credits",
    features: ["All 6 content templates", "Short, medium & long outputs", "Content history", "Priority generation"],
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$29",
    period: "/month",
    credits: "500 credits",
    features: ["Everything in Starter", "Premium output quality", "Export options", "Bulk generation", "Priority support"],
    highlight: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "$79",
    period: "/month",
    credits: "2,000 credits",
    features: ["Everything in Pro", "Custom templates", "API access", "Dedicated support", "Team features", "White-label option"],
    highlight: false,
  },
];

export default function PricingPage({ user, apiClient, onLogout }) {
  const navigate = useNavigate();
  const [loadingPkg, setLoadingPkg] = useState(null);

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setLoadingPkg(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await apiClient.post("/checkout", {
        package_id: packageId,
        origin_url: originUrl,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      setLoadingPkg(null);
    }
  };

  const content = (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-8" data-testid="pricing-page">
      <div className="text-center">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
          <span className="brand-gradient-text">Buy Credits</span>
        </h1>
        <p className="text-gray-500">Choose a plan that fits your content needs.</p>
        {user && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-500/5 border border-violet-500/10">
            <Zap className="w-4 h-4 text-violet-400" />
            <span className="text-sm">Current balance: <span className="font-semibold text-violet-300">{user.credits} credits</span></span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-gray-900/50 border rounded-xl p-6 flex flex-col ${plan.highlight ? 'border-violet-500/50 shadow-[0_0_30px_rgba(139,92,246,0.15)]' : 'border-white/5'}`}
            data-testid={`plan-card-${plan.id}`}
          >
            {plan.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-medium tracking-wide uppercase bg-violet-600 text-white px-3 py-1 rounded-full">
                Best Value
              </span>
            )}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>{plan.name}</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>{plan.price}</span>
                <span className="text-gray-500 text-sm">{plan.period}</span>
              </div>
              <p className="text-sm text-violet-400 mt-1">{plan.credits}</p>
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {plan.features.map((feat, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {feat}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handlePurchase(plan.id)}
              disabled={loadingPkg === plan.id}
              className={`w-full py-3 rounded-lg text-sm font-medium tracking-wide uppercase transition-all duration-200 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 ${plan.highlight ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white shadow-[0_0_20px_rgba(139,92,246,0.3)]' : 'border border-white/10 hover:bg-white/5 text-white'}`}
              data-testid={`buy-${plan.id}-btn`}
            >
              {loadingPkg === plan.id ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
              ) : (
                <>{plan.highlight ? <Zap className="w-4 h-4" /> : null} Buy {plan.credits}</>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div className="mt-12">
        <h2 className="text-xl font-semibold tracking-tight mb-6 text-center" style={{ fontFamily: 'Outfit' }}>Common Questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {[
            { q: "What's a credit?", a: "Each content generation uses 1 credit, regardless of template or length." },
            { q: "Do credits expire?", a: "No! Your credits never expire. Use them whenever you need." },
            { q: "Can I get a refund?", a: "Yes, we offer a 7-day money-back guarantee on all purchases." },
            { q: "What AI model do you use?", a: "We use OpenAI's latest GPT-5.2 model for the highest quality output." },
          ].map((faq, i) => (
            <div key={i} className="bg-gray-900/50 border border-white/5 rounded-xl p-5" data-testid={`faq-${i}`}>
              <h3 className="text-sm font-semibold mb-2">{faq.q}</h3>
              <p className="text-sm text-gray-400">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (user) {
    return (
      <NavShell user={user} onLogout={onLogout} active="pricing">
        {content}
      </NavShell>
    );
  }

  return <div className="min-h-screen">{content}</div>;
}
